import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { CampaignType } from "@adlywise/shared";

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

const API_BASE = import.meta.env?.VITE_API_URL ?? "/api";
const API_TOKEN = import.meta.env?.VITE_API_TOKEN;

const buildApiUrl = (path) => {
  const base = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const getStoredToken = () => {
  return localStorage.getItem("auth_token");
};

const setStoredToken = (token) => {
  if (token) {
    localStorage.setItem("auth_token", token);
  } else {
    localStorage.removeItem("auth_token");
  }
};

const toNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const ensureStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item))
    .filter((item) => item && item !== "undefined" && item !== "null");
};

export const normalizeCampaignPayload = (campaign, currentUser) => {
  if (!campaign?.name) {
    throw new Error("Campaign name is required");
  }

  const toNullableString = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    return String(value);
  };

  const payload = {
    name: campaign.name,
    description: campaign.description ?? "",
    status: campaign.status ?? "draft",
    type: campaign.type ?? CampaignType.Standard,
    selectedSegments: ensureStringArray(campaign.selectedSegments),
    userId: campaign.userId ?? currentUser ?? null,
    defaultCreativeId: toNullableString(campaign.defaultCreativeId),
    startDate: campaign.startDate ?? null,
    endDate: campaign.endDate ?? null,
    createdBy: campaign.createdBy || currentUser || "admin",
    frequency: toNullableNumber(campaign.frequency),
    icon: campaign.icon ?? null,
    cpm: toNullableNumber(campaign.cpm) ?? 0,
    dailyFrequency: toNullableNumber(campaign.dailyFrequency),
    weeklyFrequency: toNullableNumber(campaign.weeklyFrequency),
    impressionsCap: toNullableNumber(campaign.impressionsCap),
    budgetCap: toNullableNumber(campaign.budgetCap),
  };

  // Transit campaigns carry their config in `metadata`; forward it verbatim so
  // the campaign type and its config survive the save. Standard campaigns have
  // no metadata here (the wizard omits it), so we never overwrite a campaign's
  // existing import metadata and never send transit metadata for a standard
  // campaign (which the backend rejects).
  if (campaign.metadata != null) {
    payload.metadata = campaign.metadata;
  }

  for (const key of ["startDate", "endDate", "frequency"]) {
    if (payload[key] === null) delete payload[key];
  }

  return payload;
};

// Shape of the /available-data `counts` block; a stable empty default so
// consumers can index availableCounts[dim] before the fetch resolves.
const EMPTY_AVAILABLE_COUNTS = {
  country: {},
  region: {},
  city: {},
  platform: {},
  provider: {},
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserType, setCurrentUserType] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUserPredefinedFilter, setCurrentUserPredefinedFilter] =
    useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(null);
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [segmentsError, setSegmentsError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensError, setTokensError] = useState(null);
  // Traffic-source providers, fetched once at bootstrap and shared. Kept in
  // context (not in the campaign builder) so the publisher selector always has
  // options ready on both create and edit — a page-local fetch used to leave it
  // silently empty/unselectable while loading or after a transient failure.
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState(null);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  // Occurrence counts per curated selector value, used to order the static
  // country/region/city/platform/provider lists (most-populous first).
  const [availableCounts, setAvailableCounts] = useState(
    EMPTY_AVAILABLE_COUNTS,
  );
  const [availableDataLoading, setAvailableDataLoading] = useState(false);

  const performRequest = useCallback(async (path, options = {}) => {
    const headers = { ...(options.headers || {}) };
    const hasBody = options.body !== undefined && options.body !== null;

    if (hasBody && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    const token = getStoredToken() || API_TOKEN;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const fetchOptions = {
      ...options,
      headers,
      credentials: "include",
      signal: options.signal,
    };

    if (!hasBody) delete fetchOptions.body;

    const response = await fetch(buildApiUrl(path), fetchOptions);

    if (!response.ok) {
      const text = await response.text();
      let message =
        text || `Request to ${path} failed with status ${response.status}`;
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      if (parsed?.message) message = parsed.message;
      throw new Error(message);
    }

    if (response.status === 204) return null;

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) return response.json();

    return response.text();
  }, []);

  const refreshSegments = useCallback(async () => {
    setSegmentsLoading(true);
    setSegmentsError(null);
    try {
      const data = await performRequest("/segments");
      const mappedSegments = Array.isArray(data)
        ? data.map((segment) => ({
            ...segment,
            definition: segment.filters ||
              segment.definition || { all: [], any: [], none: [] },
          }))
        : [];
      setSegments(mappedSegments);
    } catch (error) {
      console.error("Failed to load segments", error);
      setSegmentsError(error);
    } finally {
      setSegmentsLoading(false);
    }
  }, [performRequest]);

  const refreshCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const response = await performRequest("/campaigns");
      const rows = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setCampaigns(rows);
    } catch (error) {
      console.error("Failed to load campaigns", error);
      setCampaignsError(error);
    } finally {
      setCampaignsLoading(false);
    }
  }, [performRequest]);

  const refreshUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await performRequest("/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load users", error);
      setUsersError(error);
    } finally {
      setUsersLoading(false);
    }
  }, [performRequest]);

  const refreshTokens = useCallback(async () => {
    setTokensLoading(true);
    setTokensError(null);
    try {
      const data = await performRequest("/access-tokens");
      setTokens(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load access tokens", error);
      setTokensError(error);
    } finally {
      setTokensLoading(false);
    }
  }, [performRequest]);

  const refreshProviders = useCallback(async () => {
    setProvidersLoading(true);
    setProvidersError(null);
    try {
      const data = await performRequest("/providers");
      setProviders(Array.isArray(data) ? data : []);
    } catch (error) {
      // Keep the last-good list on a transient failure; surface the error so the
      // publisher selector can offer a retry instead of rendering silently empty.
      console.error("Failed to load providers", error);
      setProvidersError(error);
    } finally {
      setProvidersLoading(false);
    }
  }, [performRequest]);

  const refreshAvailableData = useCallback(async () => {
    setAvailableDataLoading(true);
    try {
      const data = await performRequest("/available-data");
      setAvailableChannels(Array.isArray(data.channels) ? data.channels : []);
      setAvailableCategories(
        Array.isArray(data.categories) ? data.categories : [],
      );
      setAvailableTags(Array.isArray(data.tags) ? data.tags : []);
      setAvailableCounts(
        data.counts && typeof data.counts === "object"
          ? { ...EMPTY_AVAILABLE_COUNTS, ...data.counts }
          : EMPTY_AVAILABLE_COUNTS,
      );
    } catch (error) {
      console.error("Failed to load available filter data", error);
      setAvailableChannels([]);
      setAvailableCategories([]);
      setAvailableTags([]);
      setAvailableCounts(EMPTY_AVAILABLE_COUNTS);
    } finally {
      setAvailableDataLoading(false);
    }
  }, [performRequest]);

  const checkAuth = useCallback(async () => {
    try {
      const user = await performRequest("/auth/me");
      if (user && user.username) {
        setCurrentUser(user.username);
        setCurrentUserType(user.role);
        setCurrentUserPredefinedFilter(user.predefinedFilter ?? null);
      }
    } catch (error) {
      setStoredToken(null);
      setCurrentUser(null);
      setCurrentUserType(null);
      setCurrentUserPredefinedFilter(null);
    } finally {
      setAuthChecked(true);
    }
  }, [performRequest]);

  const login = async (username, password) => {
    try {
      const response = await performRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      if (response && response.user) {
        if (response.token) {
          setStoredToken(response.token);
        }

        setCurrentUser(response.user.username);
        setCurrentUserType(response.user.role);
        // Login response doesn't include predefinedFilter — pick it up on the
        // next /auth/me. Keep it null until then so providers fail-closed.
        setCurrentUserPredefinedFilter(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await performRequest("/auth/logout", {
        method: "POST",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setStoredToken(null);
      setCurrentUser(null);
      setCurrentUserType(null);
      setCurrentUserPredefinedFilter(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!currentUser) return;
    // Providers don't have permission on /campaigns, /segments, or /users —
    // skip those refreshes to avoid noisy 403s. They still need available-data
    // for filter options on the device list.
    if (currentUserType === "provider") {
      refreshAvailableData();
      refreshTokens();
      return;
    }
    refreshSegments();
    refreshCampaigns();
    refreshUsers();
    refreshAvailableData();
    refreshTokens();
    refreshProviders();
  }, [
    currentUser,
    currentUserType,
    refreshSegments,
    refreshCampaigns,
    refreshUsers,
    refreshAvailableData,
    refreshTokens,
    refreshProviders,
  ]);

  const addCampaign = async (campaign) => {
    const payload = normalizeCampaignPayload(campaign, currentUser);
    const created = await performRequest("/campaigns", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    setCampaigns((prev) => [...prev, created]);
    return created;
  };

  const updateCampaign = async (uuid, updates) => {
    const existing = campaigns.find((campaign) => campaign.uuid === uuid);
    if (!existing) {
      throw new Error(`Campaign with uuid ${uuid} not found`);
    }

    const payload = normalizeCampaignPayload(
      { ...existing, ...updates },
      currentUser,
    );
    const updated = await performRequest(`/campaigns/${uuid}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    setCampaigns((prev) => prev.map((c) => (c.uuid === uuid ? updated : c)));
    return updated;
  };

  const deleteCampaign = async (uuid) => {
    await performRequest(`/campaigns/${uuid}`, { method: "DELETE" });
    setCampaigns((prev) => prev.filter((c) => c.uuid !== uuid));
  };

  const buildSegmentPayload = (segment) => {
    const def = segment.definition || {};
    return {
      name: segment.name,
      description: segment.description ?? "",
      filters: {
        all: Array.isArray(def.all) ? def.all : [],
        any: Array.isArray(def.any) ? def.any : [],
        none: Array.isArray(def.none) ? def.none : [],
      },
      createdBy: segment.createdBy || currentUser || "admin",
      cpm: toNullableNumber(segment.cpm),
      kind: segment.kind ?? 2,
    };
  };

  // The UI uses `definition` while the server stores `filters`; keep both on
  // the client object so existing components don't have to choose.
  const withDefinition = (segment) => ({
    ...segment,
    definition: segment.filters ||
      segment.definition || { all: [], any: [], none: [] },
  });

  const addSegment = async (segment) => {
    const created = await performRequest("/segments", {
      method: "POST",
      body: JSON.stringify(buildSegmentPayload(segment)),
    });
    const mapped = withDefinition(created);
    setSegments((prev) => [...prev, mapped]);
    return mapped;
  };

  const updateSegment = async (uuid, updates) => {
    const updated = await performRequest(`/segments/${uuid}`, {
      method: "PUT",
      body: JSON.stringify(buildSegmentPayload(updates)),
    });
    const mapped = withDefinition(updated);
    setSegments((prev) => prev.map((s) => (s.uuid === uuid ? mapped : s)));
    return mapped;
  };

  const deleteSegment = async (uuid) => {
    await performRequest(`/segments/${uuid}`, { method: "DELETE" });
    setSegments((prev) => prev.filter((s) => s.uuid !== uuid));
  };

  const addUser = async (
    username,
    password,
    role = "user",
    predefinedFilter = null,
  ) => {
    try {
      const payload = { username, password, role };
      if (predefinedFilter !== null && predefinedFilter !== undefined) {
        payload.predefinedFilter = predefinedFilter;
      }
      const created = await performRequest("/users", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setUsers((prev) => [...prev, created]);
      return created;
    } catch (error) {
      console.error("Failed to create user", error);
      throw error;
    }
  };

  const updateUser = async (uuid, updates) => {
    try {
      const updated = await performRequest(`/users/${uuid}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      setUsers((prev) => prev.map((u) => (u.uuid === uuid ? updated : u)));
      return updated;
    } catch (error) {
      console.error("Failed to update user", error);
      throw error;
    }
  };

  const deleteUser = async (uuid) => {
    try {
      await performRequest(`/users/${uuid}`, {
        method: "DELETE",
      });
      setUsers((prev) => prev.filter((u) => u.uuid !== uuid));
    } catch (error) {
      console.error("Failed to delete user", error);
      throw error;
    }
  };

  const createToken = async (name, expiresAt = null) => {
    try {
      const payload = { name };
      if (expiresAt !== null && expiresAt !== undefined) {
        payload.expiresAt = expiresAt;
      }
      const created = await performRequest("/access-tokens", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // Append the list-item portion (without the one-time `token` field) to state
      const { token: _plaintext, ...listItem } = created;
      setTokens((prev) => [...prev, listItem]);
      return created;
    } catch (error) {
      console.error("Failed to create access token", error);
      throw error;
    }
  };

  const revokeToken = async (uuid) => {
    try {
      await performRequest(`/access-tokens/${uuid}/revoke`, { method: "POST" });
      setTokens((prev) =>
        prev.map((t) =>
          t.uuid === uuid ? { ...t, revokedAt: new Date().toISOString() } : t,
        ),
      );
    } catch (error) {
      console.error("Failed to revoke access token", error);
      throw error;
    }
  };

  const generateSegmentDefinition = async (audienceDescription) => {
    if (!audienceDescription || typeof audienceDescription !== "string") {
      throw new Error("Audience description is required");
    }

    const result = await performRequest("/segments/predict", {
      method: "POST",
      body: JSON.stringify({ description: audienceDescription }),
    });

    if (!result || typeof result !== "object" || !result.data) {
      throw new Error("AI assistant did not return a valid segment definition");
    }

    return result;
  };

  const previewSegmentCount = async (definition, kind, options = {}) => {
    try {
      const baseDefinition = definition || {};
      const filters = {
        all: Array.isArray(baseDefinition.all) ? baseDefinition.all : [],
        any: Array.isArray(baseDefinition.any) ? baseDefinition.any : [],
        none: Array.isArray(baseDefinition.none) ? baseDefinition.none : [],
      };

      const result = await performRequest("/segments/preview-count", {
        method: "POST",
        body: JSON.stringify({ filters, kind }),
        signal: options.signal,
      });

      return result?.count ?? 0;
    } catch (error) {
      if (error?.name === "AbortError") {
        throw error;
      }

      console.error("Failed to preview segment count", error);
      throw error;
    }
  };

  const getSegmentStatistics = async (segmentUuid) => {
    try {
      const result = await performRequest(
        `/segments/${segmentUuid}/statistics`,
      );
      return {
        vasts: result?.vasts ?? 0,
        totalImpressions: result?.totalImpressions ?? 0,
      };
    } catch (error) {
      console.error(
        `Failed to load statistics for segment ${segmentUuid}`,
        error,
      );
      return {
        vasts: 0,
        totalImpressions: 0,
      };
    }
  };

  const getCampaignStatistics = useCallback(
    async (campaignUuid) => {
      try {
        const result = await performRequest(
          `/campaigns/${campaignUuid}/statistics`,
        );
        return {
          totalReceivers: result?.totalReceivers ?? 0,
          uniqueReceivers: result?.uniqueReceivers ?? 0,
          spent: result?.spent ?? 0,
          confirmedImpressions: result?.confirmedImpressions ?? 0,
        };
      } catch (error) {
        console.error(
          `Failed to load statistics for campaign ${campaignUuid}`,
          error,
        );
        return {
          totalReceivers: 0,
          uniqueReceivers: 0,
          spent: 0,
          confirmedImpressions: 0,
        };
      }
    },
    [performRequest],
  );

  const getCampaignCost = useCallback(
    async (campaignUuid) => {
      if (!campaignUuid) {
        return { estimated: null, actual: null };
      }

      const result = await performRequest(`/campaigns/${campaignUuid}/cost`);

      return {
        estimated: result?.estimatedCost ?? null,
        actual: result?.realCost ?? null,
      };
    },
    [performRequest],
  );

  const submitCampaignForModeration = useCallback(
    async (campaignUuid) => {
      if (!campaignUuid) {
        throw new Error("Campaign uuid is required");
      }

      const result = await performRequest(
        `/campaigns/${campaignUuid}/submit-for-moderation`,
        {
          method: "POST",
        },
      );

      setCampaigns((prev) =>
        prev.map((c) =>
          c.uuid === campaignUuid ? { ...c, status: "pending" } : c,
        ),
      );

      return result;
    },
    [performRequest],
  );

  const approveCampaign = useCallback(
    async (campaignUuid) => {
      if (!campaignUuid) {
        throw new Error("Campaign uuid is required");
      }
      const result = await performRequest(
        `/campaigns/${campaignUuid}/approve`,
        { method: "POST" },
      );
      await refreshCampaigns();
      return result;
    },
    [performRequest, refreshCampaigns],
  );

  const downloadCampaignPlan = useCallback(
    async (campaignUuid, fallbackName = "Media Plan") => {
      if (!campaignUuid) throw new Error("Campaign uuid is required");

      const headers = {};
      const token = getStoredToken() || API_TOKEN;
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(
        buildApiUrl(`/campaigns/${campaignUuid}/plan`),
        {
          method: "GET",
          headers,
          credentials: "include",
        },
      );

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(
          message || `Failed to download plan (${response.status})`,
        );
      }

      const blob = await response.blob();

      const contentDisposition =
        response.headers.get("content-disposition") || "";
      const match =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)$/i) ||
        contentDisposition.match(/filename="([^"]+)"/i) ||
        contentDisposition.match(/filename=([^;]+)/i);

      const safeDecode = (v) => {
        try {
          return decodeURIComponent(v);
        } catch {
          return v;
        }
      };

      const filenameFromHeader = match?.[1]
        ? safeDecode(match[1].trim())
        : null;
      const filename = filenameFromHeader || `${fallbackName} Media Plan.xlsx`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      return true;
    },
    [],
  );

  const value = {
    currentUser,
    currentUserType,
    currentUserPredefinedFilter,
    authChecked,
    login,
    logout,
    campaigns,
    campaignsLoading,
    campaignsError,
    refreshCampaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    segments,
    segmentsLoading,
    segmentsError,
    refreshSegments,
    addSegment,
    updateSegment,
    deleteSegment,
    generateSegmentDefinition,
    previewSegmentCount,
    getSegmentStatistics,
    users,
    usersLoading,
    usersError,
    refreshUsers,
    addUser,
    updateUser,
    deleteUser,
    tokens,
    tokensLoading,
    tokensError,
    refreshTokens,
    createToken,
    revokeToken,
    providers,
    providersLoading,
    providersError,
    refreshProviders,
    availableChannels,
    availableCategories,
    availableTags,
    availableCounts,
    availableDataLoading,
    refreshAvailableData,
    performRequest,
    getCampaignStatistics,
    getCampaignCost,
    submitCampaignForModeration,
    approveCampaign,
    downloadCampaignPlan,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
